import React from 'react';
import { Quote } from 'lucide-react';

interface Testimonial {
  id: string;
  company: string;
  description: string;
  logo: string;
  name: string;
  position: string;
}

interface TestimonialCardProps {
  testimonial: Testimonial;
}

const TestimonialCard: React.FC<TestimonialCardProps> = ({ testimonial }) => {
  return (
    <div className="testimonial-card group relative w-full rounded-2xl bg-gradient-to-br from-gray-900/50 to-gray-800/50 backdrop-blur-xl border border-purple-500/20 hover:border-purple-500/40 transition-all duration-300 overflow-hidden">
      {/* Subtle gradient overlay on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-500/0 to-blue-500/0 group-hover:from-purple-500/5 group-hover:to-blue-500/5 transition-all duration-300" />

      <div className="relative z-10 flex flex-col h-full p-6 md:p-8">
        {/* Quote Icon */}
        <div className="mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 flex items-center justify-center opacity-80">
            <Quote className="w-6 h-6 text-white" strokeWidth={2} />
          </div>
        </div>

        {/* Testimonial Text */}
        <div className="testimonial-text-wrap flex-grow mb-6">
          <p className="testimonial-text text-gray-300 text-base leading-relaxed italic">
            "{testimonial.description}"
          </p>
        </div>

        {/* Divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-purple-500/30 to-transparent mb-6" />

        {/* Author Info */}
        <div className="flex items-center gap-3">
          {testimonial.logo && (
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-600/20 to-blue-600/20 border border-purple-500/30 flex items-center justify-center flex-shrink-0 overflow-hidden">
              <img
                src={testimonial.logo}
                alt={`${testimonial.company} logo`}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          <div className="flex-grow">
            <h4 className="text-white font-semibold text-base leading-tight mb-1">
              {testimonial.name}
            </h4>
          </div>
        </div>
      </div>

      {/* Decorative corner accent */}
      <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-purple-500/10 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

      <style>{`
        .testimonial-card {
          min-height: 420px;
          height: 100%;
        }
        .testimonial-text-wrap {
          display: flex;
          align-items: flex-start;
        }
        .testimonial-text {
          display: -webkit-box;
          -webkit-line-clamp: 6;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default TestimonialCard;